import React, { useState, useMemo, useEffect, useReducer, useRef } from 'react'
import { Select } from 'components/forms'
import { useTranslation } from 'react-i18next'
import { queryApkVersions, updateAppVersionInstall } from '../../../CashRegisters/gql/changeApk'
import DevicesTable from './DevicesTable'
import ContentBlock, { ExpandableBlocks } from 'components/ContentBlock'
import Button from 'components/Button'
import { Modal } from 'components/Modal'
import formatDate from 'helpers/dateTimeFormatter'
import shopsAndDevicesQuery from 'gql/query/cashRegistersTree'
import hardwareIdDeviceQuery from '../../gql/getDevices'
import { useQuery, useMutation } from 'components/graphql'
import Divider from 'components/Divider'
import { CardCol, CardsRow } from 'sections/statistics/pages/filterFragments/styled'
import { useSubscription } from '@apollo/client'
import { ListenDeviceStatuses } from 'sections/options/sections/GlobalCashRegisters/pages/DeviceUpdate/subscriptions/subscription'
import get from 'lodash/get'
import { filterReducer } from 'sections/statistics/pages/filterFragments'
import FilterCardLocal from 'components/FilterCard/FilterCardLocal'
import { useStartedList } from 'sections/options/sections/GlobalCashRegisters/pages/DeviceUpdate/hooks/useStartedList'
import { useFilteredData } from 'sections/options/sections/GlobalCashRegisters/pages/DeviceUpdate/hooks/useFilteredData'

const initOptions = data => data.map(item => ({
	value: item?.version,
	description: item?.description,
	label: item?.version + ' - ' + formatDate(item?.timestamp, 'DD.MM.YYYY (HH:mm:ss)')
}))

const deviceTypeFilter = (deviceData, type) => type === null ? deviceData : deviceData.filter(device => device.deviceType === type)

export const objectCheck = (obj) => typeof obj === 'object' && obj !== null && Object.values(obj).length

// limit for one query (hardwareIdDeviceQuery)
const deviceLimit = 500
const DeviceUpdate = ({ appData }) => {
	const [t] = useTranslation()
	const { data: shopsAndDevicesData } = useQuery(shopsAndDevicesQuery, { fetchPolicy: 'cache-first' })
	const { data: hardwareIdDeviceData, refetch: refetchDevices } = useQuery(hardwareIdDeviceQuery)
	const nextContent = hardwareIdDeviceData?.listDevices?.cursors?.after
	const hasNext = hardwareIdDeviceData?.listDevices?.cursors?.hasNext
	const newData = hardwareIdDeviceData?.listDevices?.items
	const { data: listenDeviceData } = useSubscription(ListenDeviceStatuses)
	const updatedDeviceData = get(listenDeviceData, 'listenDeviceStatuses', null)
	const [updateInstallVersion] = useMutation(updateAppVersionInstall, {
		onCompleted: () => {
			setCheckBoxesState({})
		}
	})
	const [cleanHardwareIdDeviceData, setCleanHardwareIdDeviceData] = useState([])
	const [appType, setAppType] = useState('SMART_CHECKOUT')
	const [fetchIndicator, setFetchIndicator] = useState(true)
	const tableDataRef = useRef([])
	useEffect(() => {
		if (fetchIndicator) {
			// when after query (hardwareIdDeviceQuery)
			// we have more devices then deviceLimit
			if (hasNext && nextContent && newData) {
				refetchDevices({
					params: {
						cursors: {
							after: nextContent,
							limit: deviceLimit
						}
					}
				})
				// comfortable array with device info
				// (appVersion, deviceStatus, appManagerPermissions...)
				setCleanHardwareIdDeviceData([
					...cleanHardwareIdDeviceData,
					...newData
				])
			} else if (newData && !hasNext) {
				// adding data from final refetch
				// or from first query (hardwareIdDeviceQuery)
				// when devices quantity < deviceLimit
				setCleanHardwareIdDeviceData([
					...cleanHardwareIdDeviceData,
					...newData
				])
				setFetchIndicator(false)
			}
		}
	}, [hardwareIdDeviceData])

	// make object that have hardwareId like key
	// and info about names (shop name, device name) like value
	const findAllDevices = (obj, out) => {
		if (objectCheck(obj) && obj?.__typename !== 'Shop' && obj?.children && Array.isArray(obj?.children) && obj?.children.length) {
			obj.children.forEach(subObject => {
				return (findAllDevices(subObject, out))
			})
		} else if (objectCheck(obj) && obj?.__typename === 'Shop' && obj?.children && Array.isArray(obj?.children) && obj?.children.length) {
			obj.children.forEach(device => {
				if (device?.hardwareId && (device?.deviceType === 'SMART_CHECKOUT' || device?.deviceType === 'MOBILE_WAITER')) {
					out[device.hardwareId] = {
						deviceName: device.name,
						shopName: obj.name,
						shopAndDeviceName: `${obj.name}: ${device.name}`,
						hardwareId: device.hardwareId,
						deviceId: device.id,
						deviceType: device.deviceType,
						tseRemoteCashRegisterName: device?.tseRemoteCashRegisterId ? obj.children.find(remoteDevice => remoteDevice.id === device?.tseRemoteCashRegisterId)?.name : null
					}
				}
			})
		}
	}

	// change check box value in state (checkBoxesState)
	const prepareDeviceToUpdate = ({ deviceHardwareId, value }) => {
		if (deviceHardwareId) {
			setCheckBoxesState({
				...checkBoxesState,
				[deviceHardwareId]: value
			})
			errorMessageCheckBox && setErrorMessageCheckBox(null)
		}
	}

	// work with all checkboxes in the same time
	const toggleAll = (isAllChecked, filteredListShopsDevices) => {
		let listShopsDevicesArr = []
		filteredListShopsDevices.length > 0 ? listShopsDevicesArr = filteredListShopsDevices : listShopsDevicesArr = Object.values(cleanHardwareIdDeviceData)
		let allIdsWithValue = {}
		isAllChecked ? listShopsDevicesArr.forEach(device => (allIdsWithValue[device.hardwareId] = true)) : listShopsDevicesArr.forEach(device => (allIdsWithValue[device.hardwareId] = false))
		setCheckBoxesState(allIdsWithValue)
	}
	const [dataTable, setDataTable] = useState([])
	useEffect(() => {
		setDataTable(deviceTypeFilter(Object.values(cleanHardwareIdDeviceData), appType === 'APP_MANAGER' ? null : appType))
	}, [appType, cleanHardwareIdDeviceData])
	const listShopsDevices = useMemo(() => {
		const out = {}
		findAllDevices(shopsAndDevicesData, out)
		return out
	}, [shopsAndDevicesData])

	// add fields (appVersion, deviceStatus, appManagerPermissions ...)
	useEffect(() => {
		if (!fetchIndicator) {
			const out = {}
			cleanHardwareIdDeviceData.length && cleanHardwareIdDeviceData.forEach(device => {
				if (objectCheck(listShopsDevices[device.hardwareId]) && 'appVersion' in device) {
					out[device.hardwareId] = {
						...listShopsDevices[device.hardwareId],
						...device,
						deviceStatus: device?.deviceStatus
					}
				}
			})
			setCleanHardwareIdDeviceData(out)
		}
	}, [fetchIndicator])

	const [checkBoxesState, setCheckBoxesState] = useState({})
	const [chosenNewVersion, setChosenNewVersion] = useState()
	const [versionDescription, setVersionDescription] = useState('')
	const [showModal, setShowModal] = useState(false)
	const [errorMessageVersion, setErrorMessageVersion] = useState()
	const [errorMessageCheckBox, setErrorMessageCheckBox] = useState()
	const [filter, dispatchFilter] = useReducer(filterReducer, {
		selectedVersions: [],
		selectedStatuses: [],
		selectedPermission: []
	})

	const listVersions = useStartedList(dataTable, updatedDeviceData, 'appVersion', t)
	const listStatuses = useStartedList(dataTable, updatedDeviceData, 'deviceStatus', t, true)
	const listPermissions = useMemo(() => [{ itemValue: true, name: 'Ja' }, { itemValue: 'null', name: 'Nein' }], [])
	useEffect(() => {
		if(updatedDeviceData) {
			const updateIndex = dataTable.findIndex(item => item.hardwareId === updatedDeviceData.sender)
			const updatedEl = { ...dataTable[updateIndex],
				deviceStatus: updatedDeviceData?.payload?.deviceStatus,
				appVersion: updatedDeviceData?.payload?.appVersion,
				appManagerPermissions: updatedDeviceData?.payload?.appManagerPermissions,
				updateProgress: updatedDeviceData?.payload?.updateProgress
			}
			const updateDataTable = [...dataTable]
			updateDataTable.splice(updateIndex, 1, updatedEl)
			setDataTable(updateDataTable)
		}
	}, [updatedDeviceData])
	const filteredTable = useFilteredData(dataTable, filter, ['appVersion', 'deviceStatus', 'appManagerPermissions'], [ filter.selectedVersions, filter.selectedStatuses, filter.selectedPermission ])

	// check checkboxes for empty (and show/delete error message)
	const checkCheckboxes = () => {
		const onlyTruehardwareIds = getOnlyTrueHardwareId() || null
		if(Array.isArray(onlyTruehardwareIds) && onlyTruehardwareIds.length) {
			setErrorMessageCheckBox(null)
		}else{
			setErrorMessageCheckBox(t('options_section.strings.error_message_device'))
		}
	}

	// when we choose new smartCheckOut version in selector this
	// function save it in the state (chosenNewVersion)
	const handleVersionChange = ({ value, description }) => {
		setVersionDescription(description)
		value && setChosenNewVersion(value)
		setErrorMessageVersion(null)
	}

	const handleTypeChange = ({ value }) => {
		setCheckBoxesState({})
		setAppType(value)
	}


	const updateDevices = () => {
		const onlyTruehardwareIds = getOnlyTrueHardwareId() || null
		if(chosenNewVersion && onlyTruehardwareIds) {
			updateInstallVersion({
				variables: {
					hardwareIds: onlyTruehardwareIds,
					version: chosenNewVersion,
					app: appType
				}
			})
			setErrorMessageVersion(null)
		}
		!chosenNewVersion ? setErrorMessageVersion(t('options_section.strings.error_message_device_version')) : setErrorMessageVersion(null)
		checkCheckboxes()
	}
	// get only true hardwareId from checkBoxesState
	const getOnlyTrueHardwareId = () => {
		if(objectCheck(checkBoxesState)) {
			const hardwareIdsArr = Object.keys(checkBoxesState)
			const onlyTruehardwareIds = hardwareIdsArr.filter((hardId) => checkBoxesState[hardId] && tableDataRef?.current.some(item => item.hardwareId === hardId))
			if(Array.isArray(onlyTruehardwareIds) && onlyTruehardwareIds.length) {
				return onlyTruehardwareIds
			}
		}
	}

	const shouldDisplayVersionDescription = appData.isAdmin && chosenNewVersion
	const applicationOptions = [{
		value: 'SMART_CHECKOUT',
		label: 'Smart Checkout'
	}, {
		value: 'MOBILE_WAITER',
		label: 'Mobile Waiter'
	}, {
		value: 'SELF_CHECKOUT',
		label: 'Self Checkout'
	}]

	if(appData.isAdmin) {
		applicationOptions.push({
			value: 'APP_MANAGER',
			label: 'App Manager'
		})
	}

	return (
		<ContentBlock label={t('options_section.sub_sections.update')}>
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<Select
					noForm
					style={{ width: 100 }}
					onChange={handleTypeChange}
					label={t('options_section.strings.type_of_app')}
					inline
					value={appType}
					options={applicationOptions}
				/>
				<Select
					key={appType}
					noForm
					style={{ width: 300 }}
					onChange={handleVersionChange}
					remoteOptions={{
						query: queryApkVersions,
						fetchPolicy: 'cache-and-network',
						variables: {
							params: { limit: 50 },
							app: appType,
							includeBeta: appData.isAdmin ? true : false
						}
					}}
					label={t('options_section.strings.version_for_update')}
					optionsEditor={initOptions}
					withVirtualBody
					inline
					simpleValue={false}
					{ ...errorMessageVersion && { error: errorMessageVersion }}
				/>
				{shouldDisplayVersionDescription &&
					<Button inline text={t('options_section.strings.version_descrition')} onClick={() => setShowModal(true)} />
				}
				{showModal &&
						<Modal
							unmount={() => {
								setShowModal(false)
							}}
							label={t('options_section.strings.version_descrition')}
							width={450}
							height={400}
						>
							{versionDescription}
						</Modal>
				}
				<Button inline text={t('options_section.strings.to_update')} onClick={updateDevices} />
			</div>
			<Divider />
			<ExpandableBlocks nested>
				<ContentBlock label={t('statistic_section.strings.filter')}>
					<CardsRow>
						<CardCol>
							<FilterCardLocal
								label='Version'
								allItemsLabel={'All versions'}
								data={listVersions}
								onChange={(value) => dispatchFilter({ type: 'selectedVersions', value })}
							/>
						</CardCol>
						<CardCol>
							<FilterCardLocal
								label='Status'
								allItemsLabel='All statuses'
								data={listStatuses}
								onChange={(value) => dispatchFilter({ type: 'selectedStatuses', value })}
							/>
						</CardCol>
						<CardCol>
							<FilterCardLocal
								label='Berechtigungen'
								allItemsLabel='All'
								data={listPermissions}
								onChange={(value) => dispatchFilter({ type: 'selectedPermission', value })}
							/>
						</CardCol>
					</CardsRow>
				</ContentBlock>
			</ExpandableBlocks>
			<Divider />
			<div>
				<DevicesTable
					key={appType}
					data={filteredTable}
					checkedItems={checkBoxesState}
					onCheckItem={prepareDeviceToUpdate}
					onCheckAll={toggleAll}
					appType={appType}
					isLoading={fetchIndicator}
					errorMessage={errorMessageCheckBox}
					dataRef={tableDataRef}
					t={t}
				/>
			</div>
		</ContentBlock>
	)
}

export default DeviceUpdate
